import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function App() {
	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState("");
	const [isTyping, setIsTyping] = useState(false);

	const bottomRef = useRef(null);
	const inputRef = useRef(null);
	const controller = useRef(null);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const sendMessage = async (customInput) => {
		const text = customInput || input;
		if (!text.trim()) return;

		const userMessage = { role: "user", content: text };
		const botMessage = { role: "assistant", content: "" };

		const updatedMessages = [...messages, userMessage];

		setMessages((prev) => [...prev, userMessage, botMessage]);
		setInput("");
		setIsTyping(true);

		try {
			controller.current = new AbortController();

			const res = await fetch(
				"https://sahayak.opt4solution.site/api/generate",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						model: "llama3.1:8b",
						prompt: buildPrompt(updatedMessages),
						stream: true,
					}),
					signal: controller.current.signal,
				},
			);

			const reader = res.body.getReader();
			const decoder = new TextDecoder();

			let fullText = "";

			while (true) {
				const { value, done } = await reader.read();
				if (done) break;

				const chunk = decoder.decode(value);
				const lines = chunk.split("\n");

				for (let line of lines) {
					if (!line.trim()) continue;

					let json;
					try {
						json = JSON.parse(line);
					} catch {
						continue;
					}

					if (json.response) {
						fullText += json.response;

						setMessages((prev) => {
							const updated = [...prev];
							updated[updated.length - 1] = {
								role: "assistant",
								content: fullText + "▌",
							};
							return updated;
						});
					}
				}
			}

			setMessages((prev) => {
				const updated = [...prev];
				updated[updated.length - 1] = {
					role: "assistant",
					content: fullText,
				};
				return updated;
			});

			setIsTyping(false);
		} catch {
			setIsTyping(false);
			setMessages((prev) => [
				...prev.slice(0, -1),
				{ role: "assistant", content: "⚠️ Error generating response" },
			]);
		}
	};

	const stopGenerating = () => {
		controller.current?.abort();
		setIsTyping(false);
	};

	const regenerate = () => {
		const lastUser = [...messages].reverse().find((m) => m.role === "user");
		if (!lastUser) return;

		setMessages((prev) => prev.slice(0, -1));
		sendMessage(lastUser.content);
	};

	const buildPrompt = (msgs) => {
		return (
			"Format responses in Markdown with headings, steps, and code blocks.\n\n" +
			msgs
				.slice(-6)
				.map((m) =>
					m.role === "user" ? `User: ${m.content}` : `Assistant: ${m.content}`,
				)
				.join("\n") +
			"\nAssistant:"
		);
	};

	const CodeBlock = ({ children }) => {
		const [copied, setCopied] = useState(false);
		const codeText = String(children).replace(/\n$/, "");

		const copy = () => {
			navigator.clipboard.writeText(codeText);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		};

		return (
			<div className="relative mb-3">
				<button
					onClick={copy}
					className="absolute top-2 right-2 text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
				>
					{copied ? "Copied!" : "Copy"}
				</button>

				<pre className="bg-gray-100 border border-gray-200 p-3 rounded-xl overflow-x-auto text-sm text-gray-800">
					<code>{codeText}</code>
				</pre>
			</div>
		);
	};

	const Message = ({ msg }) => {
		const isUser = msg.role === "user";

		return (
			<div
				className={`flex px-4 py-2 ${isUser ? "justify-end" : "justify-start"}`}
			>
				<div
					className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-6 shadow
            ${
							isUser
								? "bg-blue-600 text-white rounded-br-none"
								: "bg-white text-gray-800 border rounded-bl-none"
						}`}
				>
					<ReactMarkdown
						remarkPlugins={[remarkGfm]}
						components={{
							code({ inline, children }) {
								return inline ? (
									<code className="bg-gray-100 px-1 rounded">{children}</code>
								) : (
									<CodeBlock>{children}</CodeBlock>
								);
							},
							p: ({ children }) => <p className="mb-2">{children}</p>,
							ul: ({ children }) => (
								<ul className="list-disc ml-5 mb-2">{children}</ul>
							),
							ol: ({ children }) => (
								<ol className="list-decimal ml-5 mb-2">{children}</ol>
							),
						}}
					>
						{msg.content}
					</ReactMarkdown>
				</div>
			</div>
		);
	};

	return (
		<div className="h-screen flex flex-col bg-blue-50">
			{/* Header */}
			<div className="bg-white border-b p-4 flex items-center gap-3 shadow-sm">
				<img
					src="https://opt4solution.site/ChatBotSVG/ChatBotIcon.png"
					className="w-10 h-10"
				/>
				<div className="text-lg font-semibold text-blue-700">
					VRSamadhan Sahayak
				</div>
			</div>

			{/* Chat */}
			<div className="flex-1 overflow-y-auto">
				{messages.map((msg, i) => (
					<Message key={i} msg={msg} />
				))}

				{isTyping && (
					<div className="px-4 text-sm text-gray-400">AI is typing...</div>
				)}

				<div ref={bottomRef}></div>
			</div>

			{/* Input */}
			<div className="bg-white border-t p-4">
				<div className="flex gap-2 max-w-3xl mx-auto">
					<button
						onClick={regenerate}
						disabled={isTyping || messages.length === 0}
						className={`p-2 rounded-lg border 
              ${
								isTyping || messages.length === 0
									? "text-gray-300 border-gray-200"
									: "text-blue-600 border-blue-200 hover:bg-blue-50"
							}`}
					>
						🔄
					</button>

					<input
						ref={inputRef}
						className="flex-1 border border-blue-300 rounded-lg p-3 outline-none"
						placeholder="Ask VRSamadhan Sahayak..."
						value={input}
						onChange={(e) => setInput(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && sendMessage()}
					/>

					{isTyping ? (
						<button
							onClick={stopGenerating}
							className="bg-red-500 text-white px-4 rounded-lg"
						>
							Stop
						</button>
					) : (
						<button
							onClick={() => sendMessage()}
							className="bg-blue-600 text-white px-4 rounded-lg"
						>
							Send
						</button>
					)}
				</div>
			</div>
		</div>
	);
}
